import { useEffect, useState, RefObject } from "react"

/**
 * Hook to calculate if text needs to be truncated based on container width
 *
 * @param containerRef The container element reference to measure against
 * @param fullText The complete text to measure
 * @param shouldMeasure Whether the measurement should be performed
 * @returns Whether the text should be truncated
 */
export const useTruncation = (
    containerRef: RefObject<HTMLElement>,
    fullText: string,
    shouldMeasure: boolean,
): boolean => {
    const [shouldTruncate, setShouldTruncate] = useState(false)

    useEffect(() => {
        if (!shouldMeasure || containerRef.current == null) return

        // Simple measurement function
        const checkTruncation = () => {
            if (containerRef.current == null) return

            // Create temp measurement span
            const span = document.createElement("span")
            span.style.visibility = "hidden"
            span.style.position = "absolute"
            span.style.fontSize = "12px"
            span.style.whiteSpace = "nowrap"
            span.textContent = fullText

            document.body.appendChild(span)
            const needsTruncate =
                span.getBoundingClientRect().width >
                containerRef.current.clientWidth - 20
            document.body.removeChild(span)

            setShouldTruncate(needsTruncate)
        }

        // Set up observer with debounced checks
        const observer = new ResizeObserver(() => {
            window.requestAnimationFrame(checkTruncation)
        })

        observer.observe(containerRef.current)
        checkTruncation() // Initial check

        return () => observer.disconnect()
    }, [shouldMeasure, fullText, containerRef])

    return shouldTruncate
}
